import { Component, EventEmitter, Output, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule, FormArray } from '@angular/forms';
import { ApiService } from 'app/services/api.service';
import { TranslateModule } from '@ngx-translate/core';

import { INightowlCustomerId } from 'app/models/device.model';  
@Component({
    selector: 'nightowl-service-customer-id',
    templateUrl: './nightowl-service-customer-id.component.html',
    styleUrls: ['./nightowl-service-customer-id.component.css'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [FormsModule, ReactiveFormsModule, TranslateModule]
})
export class NightowlServiceCustomerIdComponent implements OnInit, OnDestroy {

  @Output() closeFormEvent = new EventEmitter<{ success: boolean }>();

  customerIdForm: UntypedFormGroup;
  saveSuccess = false;
  saveError = false;
  duplicateIdError = false;
  duplicateNameError = false;
  storedCustomerIds: INightowlCustomerId[] = []; // Store original data with both id and name

  constructor(
    private api: ApiService,
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    // create a FormArray for multiple id/name pairs
    this.customerIdForm = this.fb.group({
      customerIds: this.fb.array([])
    });

    this.api.getNightowlCustomerId().subscribe((response) => {
      const customerIdsArray = this.customerIdForm.get('customerIds') as FormArray;
      customerIdsArray.clear();

      let customerIds: INightowlCustomerId[] = [];

      // Handle both formats: string or array of objects
      if (typeof response === 'string') {
        // Legacy format: comma-separated string
        const ids = response ? response.split(',') : [];
        customerIds = ids.filter(id => id.trim()).map(id => ({
          id: id.trim(),
          name: ""
        }));
      } else if (Array.isArray(response)) {
        // New format: array of objects with id and name
        customerIds = response;
      }

      // Store the original data
      this.storedCustomerIds = [...customerIds];

      // Add existing customer IDs as controls
      if (customerIds.length > 0) {
        customerIds.forEach(customerIdObj => {
          const hasName = customerIdObj.name && customerIdObj.name.trim() !== '';
          customerIdsArray.push(this.fb.group({
            id: [{ value: customerIdObj.id, disabled: true }, Validators.required],
            // Name field is enabled if empty, disabled if it has a value
            name: [{ value: customerIdObj.name || '', disabled: hasName }, hasName ? Validators.required : []]
          }));
        });
      } else {
        // Only add empty field if there are no existing entries
        customerIdsArray.push(this.fb.group({
          id: ['', Validators.required],
          name: [''] // No validator initially
        }));
      }
    }); 
  }

  get customerIdsArray(): FormArray {
    return this.customerIdForm.get('customerIds') as FormArray;
  }

  isNameDifferentFromId(index: number): boolean {
    const group = this.customerIdsArray.at(index) as UntypedFormGroup;
    const id = group.get('id')?.value;
    const name = group.get('name')?.value;
    return id && name && id !== name;
  }

  shouldShowNameField(): boolean {
    // Show name field if there are multiple entries (existing + new)
    return this.customerIdsArray.length > 1;
  }

  hasDuplicates(): { hasDuplicateIds: boolean; hasDuplicateNames: boolean } {
    const allEntries = this.customerIdsArray.getRawValue() as Array<{id: string, name: string}>;
    const ids = allEntries
      .map(e => e.id)
      .filter(id => id && id.trim() !== '')
      .map(id => id.trim().toLowerCase());
    const names = allEntries
      .map(e => e.name)
      .filter(name => name && name.trim() !== '')
      .map(name => name.trim().toLowerCase());
    
    const hasDuplicateIds = ids.length !== new Set(ids).size;
    const hasDuplicateNames = names.length !== new Set(names).size;
    
    return { hasDuplicateIds, hasDuplicateNames };
  }

  removeCustomerId(index: number): void {
    this.customerIdsArray.removeAt(index);
  }

  get isLastCustomerIncomplete(): boolean {
    const arr = this.customerIdsArray;
    if (!arr.length) return true;
    const lastGroup = arr.at(arr.length - 1) as UntypedFormGroup;
    const id = lastGroup.get('id')?.value;
    const name = lastGroup.get('name')?.value;
    
    // If only one entry (no existing ones), only check ID
    if (arr.length === 1) {
      return !id || id.trim() === '';
    }
    
    // If multiple entries, check both ID and name
    return (!id || id.trim() === '') || (!name || name.trim() === '');
  }

  saveCustomerId(): void {
   if (this.customerIdForm.invalid) return;

    // Check for duplicates
    const duplicates = this.hasDuplicates();
    this.duplicateIdError = duplicates.hasDuplicateIds;
    this.duplicateNameError = duplicates.hasDuplicateNames;
    
    if (this.duplicateIdError || this.duplicateNameError) {
      return;
    }

    // Get all customer IDs from the form
    const allEntries = this.customerIdsArray.getRawValue() as Array<{id: string, name: string}>;
    
    // Build the final array of customer IDs
    const customerIds: INightowlCustomerId[] = [];
    
    allEntries.forEach((entry, index) => {
      // Skip completely empty entries
      if (!entry.id || entry.id.trim() === '') {
        return;
      }
      
      customerIds.push({
        id: entry.id.trim(),
        name: entry.name && entry.name.trim() ? entry.name.trim() : ''
      });
    });

    this.api.saveNightowlCustomerId(customerIds).subscribe({
      next: () => {
        this.saveSuccess = true;
        this.saveError = false;
        this.duplicateIdError = false;
        this.duplicateNameError = false;
        this.closeForm();
      },
      error: () => {
        this.saveSuccess = false;
        this.saveError = true;
        this.duplicateIdError = false;
        this.duplicateNameError = false;
      }
    });
  }

  addCustomerId(): void {
    // If this will be the second entry, add name validators to existing controls
    if (this.customerIdsArray.length === 1) {
      this.customerIdsArray.controls.forEach(control => {
        const group = control as UntypedFormGroup;
        const nameControl = group.get('name');
        if (nameControl && !nameControl.disabled) {
          nameControl.setValidators(Validators.required);
          nameControl.updateValueAndValidity();
        }
      });
    }

    // Add new entry with name required
    this.customerIdsArray.push(this.fb.group({
      id: ['', Validators.required],
      name: ['', this.customerIdsArray.length >= 1 ? Validators.required : []]
    }));
  }

  closeForm(): void {
    this.closeFormEvent.emit({ success: this.saveSuccess });
  }

  ngOnDestroy(): void {}
}